"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Paires "Sans PHP" -> "Avec PHP", alignées par index.
const PAIRS = [
    { before: "Php_contrast_without_1", after: "Php_contrast_with_1" },
    { before: "Php_contrast_without_2", after: "Php_contrast_with_2" },
    { before: "Php_contrast_without_3", after: "Php_contrast_with_3" },
    { before: "Php_contrast_without_4", after: "Php_contrast_with_4" },
];

const listContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } },
};

const listItem = {
    hidden: { opacity: 0, y: 14 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.45, ease: "easeOut" },
    },
};

export default function PhpContrast() {
    const t = useTranslations();

    return (
        <section id="php-contrast" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Php_contrast_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Php_contrast_intro")}
            </p>

            <div className="mt-10 relative grid lg:grid-cols-2 gap-6 lg:gap-8">
                {/* SANS PHP */}
                <motion.div
                    initial={{ opacity: 0, x: -24 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="relative rounded-3xl border border-gray-200 dark:border-borderDark/80 bg-gray-50 dark:bg-bgDark-500 p-7 sm:p-9 saturate-[0.85]"
                >
                    <div className="inline-flex items-center gap-2 rounded-full bg-gray-200/70 dark:bg-white/5 ring-1 ring-gray-300/60 dark:ring-white/10 px-3 py-1 mb-6">
                        <SolarIcon
                            name="close-circle-bold-duotone"
                            className="w-4 h-4 text-gray-500 dark:text-grayDark-400"
                        />
                        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-grayDark-400">
                            {t("Php_contrast_without_label")}
                        </span>
                    </div>

                    <motion.ul
                        variants={listContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        className="flex flex-col gap-4"
                    >
                        {PAIRS.map((pair, index) => (
                            <motion.li
                                key={`without-${index}`}
                                variants={listItem}
                                className="flex items-start gap-3"
                            >
                                <SolarIcon
                                    name="close-circle-bold-duotone"
                                    className="w-5 h-5 text-gray-400 dark:text-grayDark-500 shrink-0 mt-0.5"
                                />
                                <span className="text-[15px] text-gray-500 dark:text-lightBlue/70 leading-snug">
                                    {t(pair.before)}
                                </span>
                            </motion.li>
                        ))}
                    </motion.ul>
                </motion.div>

                {/* Badge VS */}
                <div className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none">
                    <motion.div
                        initial={{ scale: 0, rotate: -12 }}
                        whileInView={{ scale: 1, rotate: 0 }}
                        viewport={{ once: true }}
                        transition={{
                            type: "spring",
                            stiffness: 260,
                            damping: 16,
                            delay: 0.25,
                        }}
                        className="flex items-center justify-center w-12 h-12 rounded-full bg-white dark:bg-bgDark-700 shadow-xl ring-1 ring-gray-200 dark:ring-white/10"
                    >
                        <span className="text-xs font-extrabold tracking-wider text-gray-400 dark:text-grayDark-500">
                            {t("Php_contrast_vs")}
                        </span>
                    </motion.div>
                </div>

                {/* AVEC PHP & MySQL */}
                <motion.div
                    initial={{ opacity: 0, x: 24, scale: 0.98 }}
                    whileInView={{ opacity: 1, x: 0, scale: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.5, ease: "easeOut", delay: 0.15 }}
                    className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10 p-7 sm:p-9"
                >
                    {/* Glows (teinte PHP) */}
                    <div className="absolute -top-20 -right-20 w-[400px] h-[400px] bg-indigo-500/20 rounded-full blur-3xl" />
                    <div className="absolute -bottom-24 -left-16 w-[320px] h-[320px] bg-blue-500/10 rounded-full blur-3xl" />

                    <div className="relative">
                        <div className="inline-flex items-center gap-2 rounded-full bg-indigo-500/15 ring-1 ring-indigo-400/30 px-3 py-1 mb-6">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-400" />
                            </span>
                            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-indigo-300">
                                {t("Php_contrast_with_label")}
                            </span>
                        </div>

                        <motion.ul
                            variants={listContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-80px" }}
                            className="flex flex-col gap-4"
                        >
                            {PAIRS.map((pair, index) => (
                                <motion.li
                                    key={`with-${index}`}
                                    variants={listItem}
                                    className="flex items-start gap-3"
                                >
                                    <SolarIcon
                                        name="check-circle-bold-duotone"
                                        className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5"
                                    />
                                    <span className="text-[15px] text-gray-100 leading-snug">
                                        {t(pair.after)}
                                    </span>
                                </motion.li>
                            ))}
                        </motion.ul>

                        {/* Encart résultat */}
                        <div className="mt-7 rounded-2xl bg-white/[0.06] ring-1 ring-white/10 px-5 py-4">
                            <div className="flex items-center gap-2 mb-1">
                                <SolarIcon
                                    name="bolt-bold-duotone"
                                    className="w-4 h-4 text-indigo-300"
                                />
                                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-indigo-300">
                                    {t("Php_contrast_result_label")}
                                </span>
                            </div>
                            <p className="text-[15px] sm:text-base font-medium text-white leading-snug mb-0">
                                {t("Php_contrast_result_text")}
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
